<template>
  <v-card flat>
    <v-card-title>
      Meal Sales Report
      <v-spacer/>
      <v-btn outlined class="mr-2" @click="downloadCsv" :disabled="rowsForTable.length === 0">
        <v-icon left small>mdi-download</v-icon>
        Download CSV
      </v-btn>
      <v-btn outlined @click="fetchReport" :loading="loading">Refresh</v-btn>
    </v-card-title>

    <v-card-actions>
      <v-menu
        v-model="fromMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{on, attrs}">
          <v-text-field
            label="From week (Sun)"
            :value="from"
            readonly
            dense
            hide-details
            style="max-width: 200px;"
            class="mr-4"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="from"
          :allowed-dates="isSunday"
          first-day-of-week="0"
          @input="fromMenu = false; fetchReport()"
        />
      </v-menu>
      <v-menu
        v-model="toMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{on, attrs}">
          <v-text-field
            label="To week (Sun)"
            :value="to"
            readonly
            dense
            hide-details
            style="max-width: 200px;"
            class="mr-4"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="to"
          :allowed-dates="isSunday"
          first-day-of-week="0"
          @input="toMenu = false; fetchReport()"
        />
      </v-menu>
      <v-btn small text @click="setRange(5)">Last 5 weeks</v-btn>
      <v-btn small text @click="setRange(8)">Last 8 weeks</v-btn>
      <v-btn small text @click="setRange(13)">Last 13 weeks</v-btn>
      <v-spacer/>
      <v-btn
        small
        :outlined="!weeklyBreakdown"
        :color="weeklyBreakdown ? 'primary' : ''"
        :dark="weeklyBreakdown"
        @click="toggleWeekly"
      >
        <v-icon left small>{{ weeklyBreakdown ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline' }}</v-icon>
        Breakdown by week
      </v-btn>
    </v-card-actions>

    <v-card-actions>
      <v-text-field
        v-model="search"
        label="Search"
        prepend-inner-icon="mdi-magnify"
        dense
        hide-details
        clearable
        style="max-width: 240px;"
      />
    </v-card-actions>

    <v-card-text>
      <div v-if="totalQty !== null" class="text-caption mb-2" style="color:#666;">
        {{ rowsForTable.length }} chef's choice meals — {{ totalQty }} units total between
        {{ rangeInfo.from }} and {{ rangeInfo.to }}
      </div>
      <v-data-table
        :items="rowsForTable"
        :headers="headers"
        :loading="loading"
        :search="search"
        :sort-by="['quantity']"
        :sort-desc="[true]"
        dense
        :items-per-page="50"
        :footer-props="{itemsPerPageOptions: [25, 50, 100, -1]}"
      >
        <template v-slot:item.name="{item}">{{ item.name }}</template>
        <template v-slot:item.first_date="{item}">{{ formatDate(item.first_date) }}</template>
        <template v-slot:item.last_date="{item}">{{ formatDate(item.last_date) }}</template>
        <template
          v-for="w in weeks"
          v-slot:[`item.week_${w}`]="{item}"
        >
          <span :key="'cell-'+w" :style="(item.weekly && item.weekly[w]) ? '' : 'color:#ccc;'">
            {{ (item.weekly && item.weekly[w]) || 0 }}
          </span>
        </template>
      </v-data-table>
    </v-card-text>

    <v-snackbar v-model="snackbar" :timeout="3000" color="error">
      {{ snackbarText }}
    </v-snackbar>
  </v-card>
</template>

<script>
import api from "@/api";
import moment from "moment";

function toSunday(m) {
  const d = m.clone();
  return d.subtract(d.day(), 'days').format('YYYY-MM-DD');
}

function csvEscape(value) {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export default {
  name: "MenuSalesReport",
  props: {
    menu: {type: Array, default: () => []},
  },
  data() {
    return {
      loading: false,
      to: toSunday(moment()),
      from: moment(toSunday(moment())).subtract(4, 'weeks').format('YYYY-MM-DD'),
      fromMenu: false,
      toMenu: false,
      rows: [],
      weeks: [],
      weeklyBreakdown: false,
      rangeInfo: {from: null, to: null},
      search: '',
      snackbar: false,
      snackbarText: '',
    };
  },
  computed: {
    mealLookup() {
      const map = {};
      for (const m of this.menu) {
        if (!m || m.id == null) continue;
        const key = String(m.id).replace(/CC$/, '');
        if (!map[key]) map[key] = {name: m.name, category: m.category};
      }
      return map;
    },
    rowsForTable() {
      return this.rows
        .filter(r => !!this.mealLookup[String(r.meal_id)])
        .map(r => {
          const enrich = this.mealLookup[String(r.meal_id)] || {};
          return {
            ...r,
            name: enrich.name || '',
            category: enrich.category || '',
          };
        });
    },
    totalQty() {
      if (!this.rowsForTable.length) return null;
      return this.rowsForTable.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
    },
    columnTotals() {
      const totals = {quantity: 0, line_items: 0, weekly: {}};
      for (const r of this.rowsForTable) {
        totals.quantity += Number(r.quantity) || 0;
        totals.line_items += Number(r.line_items) || 0;
        for (const w of this.weeks) {
          totals.weekly[w] = (totals.weekly[w] || 0) + ((r.weekly && Number(r.weekly[w])) || 0);
        }
      }
      return totals;
    },
    headers() {
      const t = this.columnTotals;
      const base = [
        {value: 'meal_id', text: 'Meal ID', width: '90px'},
        {value: 'name', text: 'Name'},
        {value: 'category', text: 'Category', width: '120px'},
        {value: 'quantity', text: `Quantity \n(${t.quantity})`, width: '110px', align: 'end'},
        // {value: 'line_items', text: `LineItems \n(${t.line_items})`, width: '110px', align: 'end'},
      ];
      const weekCols = (this.weeklyBreakdown ? this.weeks : []).map(w => ({
        value: `week_${w}`,
        text: `${moment(w).format('DD MMM')} \n(${t.weekly[w] || 0})`,
        width: '90px',
        align: 'end',
        sort: (a, b) => (a || 0) - (b || 0),
        sortable: true,
      }));
      const tail = [
        {value: 'first_date', text: 'First', width: '120px'},
        {value: 'last_date', text: 'Last', width: '120px'},
      ];
      return [...base, ...weekCols, ...tail];
    },
  },
  mounted() {
    return this.fetchReport();
  },
  methods: {
    formatDate(d) {
      if (!d) return '';
      return moment.utc(d).format('YYYY-MM-DD');
    },
    isSunday(val) {
      return moment(val).day() === 0;
    },
    setRange(weeks) {
      const to = toSunday(moment());
      this.to = to;
      this.from = moment(to).subtract(weeks - 1, 'weeks').format('YYYY-MM-DD');
      this.fetchReport();
    },
    toggleWeekly() {
      this.weeklyBreakdown = !this.weeklyBreakdown;
      this.fetchReport();
    },
    async fetchReport() {
      this.loading = true;
      try {
        const params = {
          from: this.from,
          to: moment(this.to).endOf('week').format('YYYY-MM-DD'),
        };
        if (this.weeklyBreakdown) params.breakdown = 'week';
        const {data} = await api.get('v2/reports/sales', {params});
        this.rows = Array.isArray(data.meals) ? data.meals : [];
        this.weeks = Array.isArray(data.weeks) ? data.weeks : [];
        this.rangeInfo = {from: data.from, to: data.to};
      } catch (e) {
        console.error('failed to load sales report', e);
        this.snackbarText = 'Error loading sales report';
        this.snackbar = true;
      } finally {
        this.loading = false;
      }
    },
    downloadCsv() {
      const rows = this.rowsForTable;
      if (rows.length === 0) return;
      const weekHeaders = this.weeklyBreakdown ? this.weeks : [];
      const headerCols = [
        'Meal ID', 'Name', 'Category', 'Quantity', 'Line Items',
        ...weekHeaders.map(w => `Week of ${w}`),
        'First', 'Last',
      ];
      const lines = [headerCols.map(csvEscape).join(',')];
      for (const r of rows) {
        const row = [
          r.meal_id, r.name, r.category, r.quantity, r.line_items,
          ...weekHeaders.map(w => (r.weekly && r.weekly[w]) || 0),
          this.formatDate(r.first_date), this.formatDate(r.last_date),
        ];
        lines.push(row.map(csvEscape).join(','));
      }
      const csv = lines.join('\n');
      const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `meal-sales_${this.from}_to_${this.to}${this.weeklyBreakdown ? '_weekly' : ''}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>
